import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useEffect, useId } from 'react';

import { supabase } from '@/lib/supabase/client';

/**
 * Subscribe to Postgres changes on a table and invalidate the given query
 * keys when anything lands. The DB stays the source of truth - realtime is
 * purely an invalidation signal, so client state can never drift.
 *
 * Channel names must be unique per live subscription: supabase-js returns the
 * EXISTING channel instance for a reused name, and adding postgres_changes
 * callbacks to an already-subscribed channel throws ("cannot add callbacks
 * after subscribe"). React's useId gives a stable per-mount suffix, which
 * makes remounts and fast-refresh safe.
 */
export function useRealtimeInvalidation(options: {
  channel: string;
  table: string;
  /** PostgREST-style filter, e.g. `fixture_id=eq.<uuid>`. */
  filter?: string;
  invalidate: QueryKey[];
  enabled?: boolean;
}) {
  const { channel, table, filter, enabled = true } = options;
  const queryClient = useQueryClient();
  // Serialized so a fresh array literal per render doesn't resubscribe.
  const invalidateJson = JSON.stringify(options.invalidate);
  const instanceId = useId();

  useEffect(() => {
    if (!supabase || !enabled) return;
    const keys = JSON.parse(invalidateJson) as QueryKey[];
    const client = supabase;
    // A sync run inserts/updates many rows in a burst (e.g. all events of a live
    // match). Debounce so the burst triggers ONE invalidation instead of N
    // refetches. invalidateQueries defaults to refetchType:'active', so only
    // mounted queries refetch - inactive cached day tabs just go stale.
    let timer: ReturnType<typeof setTimeout> | null = null;
    const flush = () => {
      timer = null;
      for (const key of keys) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    };
    const subscription = client
      .channel(`${channel}:${instanceId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table, ...(filter ? { filter } : {}) },
        () => {
          if (timer) clearTimeout(timer);
          timer = setTimeout(flush, 800);
        },
      )
      .subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      client.removeChannel(subscription).catch(() => {
        // Channel already gone - nothing to clean up.
      });
    };
  }, [channel, table, filter, enabled, queryClient, invalidateJson, instanceId]);
}
